import { useLayoutEffect, useMemo } from "react";

import { getJSON } from "./httpRequestUtil";
import { useLocation } from "react-router-dom";

type ViteManifest = Record<string, { file?: string }>;

// A custom hook that builds on useLocation to parse
// the query string for you.
export function useQueryParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

/**
 * A function that checks for updates to the manifest index hashing
 * and reloads the page if there are updates
 * Uses the placeholder manifest.json file on dev mode
 */
export function reloadIfUpdates() {
  const hashFile = sessionStorage.getItem("index-hash-file");
  if (hashFile) {
    getJSON<ViteManifest>("/manifest.json").then((manifest) => {
      if (!manifest["index.html"]["file"]) {
        return;
      }
      const currentHashedFile = manifest["index.html"]["file"];
      if (currentHashedFile !== hashFile) {
        console.log("A new version of the site is available. Reloading...");
        sessionStorage.setItem("index-hash-file", currentHashedFile);
        window.location.reload();
      }
    });
  } else {
    getJSON<ViteManifest>("/manifest.json").then((manifest) => {
      if (!manifest["index.html"]["file"]) {
        return;
      }
      sessionStorage.setItem("index-hash-file", manifest["index.html"]["file"]);
    });
  }
}

/**
 * A custom hook that checks for updates on pathname changes,
 * reloads the page if there are updates to the manifest index hashing.
 * Only triggers on pathname changes (not query params or hash) to avoid
 * disrupting user workflows like pagination or filtering.
 * */
export function useVersionCheck() {
  const location = useLocation();
  useLayoutEffect(() => {
    reloadIfUpdates();
  }, [location.pathname]); // Only trigger on pathname changes, not search or hash
}
